import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { CookieConsent } from "./components/CookieConsent";
import { FullscreenLoader } from "./components/FullscreenLoader";
import { FloatingWidget } from "./components/FloatingWidget";
import { SmoothCursor } from "./components/SmoothCursor";
import { HomePage } from "./pages/HomePage";
import { Services } from "./pages/Services";
import { Courses } from "./pages/Courses";
import { About } from "./pages/About";
import { PricingPage } from "./pages/PricingPage";
import { SecurityPage } from "./pages/SecurityPage";
import { Careers } from "./pages/Careers";
import { ContactPage } from "./pages/ContactPage";
import { BlogDirectory } from "./pages/BlogDirectory";
import { BlogPostPage } from "./pages/BlogPostPage";
import { NewsPage } from "./pages/NewsPage";
import { DocumentPage } from "./pages/DocumentPage";
import { AdminPage } from "./pages/AdminPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ServerErrorPage } from "./pages/ServerErrorPage";

import privacyDoc from "../../docs/privacy_policy.md";
import dataStorageDoc from "../../docs/data_storage_policy.md";
import termsDoc from "../../docs/terms_and_conditions.md";
import amcDoc from "../../docs/amc.md";

import { useRoute, scrollToElement } from "./hooks/useRoute";
import { getFeatureFlags, getPageStatuses, type FeatureFlags, type PageStatus } from "./utils/adminStore";
import { injectStructuredData, getOrganizationSchema } from "./utils/seo";
import "./index.css";

const routeSeoMetadata: Record<string, { title: string; description: string }> = {
  "/": {
    title: "RADIECE | Intelligence, Engineered.",
    description: "RADIECE PRIVATE LIMITED - Research And Development Infrastructure for Electronic and Computing Equipment. Delivering IT & ITeS, SaaS & Platforms (RaBuOS), Applied R&D, Robotics & Automation, Security Systems, and Technical Training.",
  },
  "/services": {
    title: "Services & Engineering Capabilities | RADIECE",
    description: "Enterprise technology engineering across IT & ITeS, SaaS Digital Platforms (RaBuOS), Applied R&D, Robotics & IoT Embedded Systems, Cyber Security, and Technical Education.",
  },
  "/courses": {
    title: "Technology Courses & Training | RADIECE",
    description: "Hands-on industry engineering courses in Full-Stack Development, Embedded Systems & IoT, AI/ML Engineering, Cyber Security, and Cloud Architecture.",
  },
  "/blog": {
    title: "Technical Blog & Systems Architecture | RADIECE",
    description: "Deep engineering publications on RaBuOS platform architecture, LoRaWAN wireless telemetry, DPDP compliance, and modern 32-bit embedded systems.",
  },
  "/news": {
    title: "Newsfeed, Releases & Corporate Dispatches | RADIECE",
    description: "Official press releases, platform release notes, firmware dispatches, and statutory notices from RADIECE PRIVATE LIMITED.",
  },
  "/pricing": {
    title: "Transparent Pricing Schedules | RADIECE",
    description: "Transparent engineering pricing for static websites, custom web applications, e-commerce platforms, SaaS platforms, and enterprise software maintenance.",
  },
  "/careers": {
    title: "Careers & Open Positions | RADIECE",
    description: "Join the multidisciplinary engineering team at RADIECE. Explore open roles across UI/UX design, full-stack software development, embedded systems, and AI.",
  },
  "/about": {
    title: "About Us & Governance | RADIECE PRIVATE LIMITED",
    description: "Learn about RADIECE PRIVATE LIMITED, our engineer-led mindset, founding principles, corporate leadership, and registered office in Kakkanad, Ernakulam, Kerala.",
  },
  "/contact": {
    title: "Contact & Project Intake Desk | RADIECE",
    description: "Get in touch with the engineering and client relations team at RADIECE for enterprise solutions, consultations, training inquiries, or custom R&D.",
  },
  "/security": {
    title: "Security Disclosure Policy & Bug Bounty | RADIECE",
    description: "Security vulnerability disclosure policy, bug bounty intake, safe harbor guidelines, and incident triage response at RADIECE.",
  },
  "/amc": {
    title: "Annual Maintenance Contracts (AMC) | RADIECE",
    description: "Comprehensive Annual Maintenance Contracts (AMC), system health audits, proactive security patches, and 24/7 SLA infrastructure support.",
  },
  "/terms": {
    title: "Terms and Conditions | RADIECE PRIVATE LIMITED",
    description: "Master service terms, intellectual property ownership, warranty policies, payment terms, and legal agreements for RADIECE.",
  },
  "/privacy": {
    title: "Privacy Policy | RADIECE PRIVATE LIMITED",
    description: "Data protection and privacy policy aligned with the Digital Personal Data Protection (DPDP) Act 2023 and global privacy standards.",
  },
  "/data-storage": {
    title: "Data Storage & Encryption Policy | RADIECE",
    description: "Data storage architecture, AES-256 encryption at rest and in transit, backup lifecycle management, and CERT-In compliance specifications.",
  },
  "/admin": {
    title: "Admin Dashboard | RADIECE Management",
    description: "Internal administrative management console for RADIECE Private Limited.",
  },
  "/404": {
    title: "404 — Page Not Found | RADIECE PRIVATE LIMITED",
    description: "The requested route does not exist or has been relocated.",
  },
  "/500": {
    title: "500 — Subsystem Error | RADIECE PRIVATE LIMITED",
    description: "An unexpected runtime condition occurred in this client subsystem.",
  },
};

export function App() {
  const { path, section } = useRoute();
  const [flags, setFlags] = useState<FeatureFlags>(getFeatureFlags());
  const [pageStatuses, setPageStatuses] = useState<PageStatus[]>(getPageStatuses());

  useEffect(() => {
    const meta = routeSeoMetadata[path] ?? routeSeoMetadata["/"]!;
    document.title = meta.title;

    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", meta.description);
    }

    // Update OG metadata
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", meta.title);

    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", meta.description);

    const fullUrl = `https://www.radiece.com${path === "/" ? "" : path}${section ? `/${section}` : ""}`;
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", fullUrl);

    // Update Canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute("href", fullUrl);
    }

    // Default Organization JSON-LD injection on root/core pages
    if (path !== "/blog" && path !== "/news") {
      injectStructuredData(getOrganizationSchema());
    }

    // Scroll handling: if hash or section is present, let scrollToElement handle it; otherwise scroll to top
    if (typeof window !== "undefined") {
      const hashAnchor = window.location.hash.slice(1);
      if (hashAnchor) {
        scrollToElement(hashAnchor);
      } else if (section && path !== "/blog" && path !== "/news") {
        scrollToElement(section);
      } else {
        window.scrollTo({ top: 0, behavior: "instant" });
      }
    }
  }, [path, section]);

  useEffect(() => {
    const sync = () => {
      setFlags(getFeatureFlags());
      setPageStatuses(getPageStatuses());
    };
    window.addEventListener("radiece_admin_update", sync);
    return () => window.removeEventListener("radiece_admin_update", sync);
  }, []);

  // Check if current route is marked as Draft
  const targetPage = pageStatuses.find((p) => p.path === path);
  const isDraft = (targetPage && targetPage.status === "draft" && path !== "/admin") ||
    (path === "/careers" && !flags.enableCareers) ||
    (path === "/pricing" && !flags.enablePricingCalculator) ||
    (path === "/blog" && !flags.enableBlog) ||
    (path === "/news" && !flags.enableNews);

  const renderPage = () => {
    if (isDraft) {
      return (
        <div className="py-24 text-center px-4">
          <div className="max-w-md mx-auto bg-slate-800/80 border border-slate-700 p-8 rounded-2xl">
            <h1 className="text-2xl font-bold text-white mb-2">Page Under Maintenance</h1>
            <p className="text-slate-400 text-sm mb-6">
              This page is currently being updated by our engineering team. Please check back shortly.
            </p>
            <a
              href="/"
              className="inline-block px-5 py-2.5 bg-primary text-slate-950 font-semibold rounded-lg text-sm transition-all hover:bg-sky-400"
            >
              Return to Homepage
            </a>
          </div>
        </div>
      );
    }

    switch (path) {
      case "/":
        return <HomePage />;
      case "/admin":
        return <AdminPage />;
      case "/services":
        return <Services scrollTo={section || undefined} />;
      case "/courses":
        return <Courses scrollTo={section || undefined} />;
      case "/blog":
        return section ? <BlogPostPage slug={section} /> : <BlogDirectory />;
      case "/news":
        return <NewsPage slug={section || undefined} />;
      case "/about":
        return <About scrollTo={section || undefined} />;
      case "/pricing":
        return <PricingPage scrollTo={section || undefined} />;
      case "/careers":
        return <Careers />;
      case "/contact":
        return <ContactPage />;
      case "/privacy":
        return <DocumentPage content={privacyDoc} scrollTo={section || undefined} />;
      case "/data-storage":
        return <DocumentPage content={dataStorageDoc} scrollTo={section || undefined} />;
      case "/security":
        return <SecurityPage />;
      case "/terms":
        return <DocumentPage content={termsDoc} scrollTo={section || undefined} />;
      case "/amc":
        return <DocumentPage content={amcDoc} scrollTo={section || undefined} />;
      case "/500":
        return <ServerErrorPage />;
      case "/404":
        return <NotFoundPage currentPath={path} />;
      default:
        return <NotFoundPage currentPath={path} />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-[#090e17] text-slate-900 dark:text-slate-100 transition-colors">
      {/* Modern Fullscreen Brand Loader */}
      <FullscreenLoader minDurationMs={750} />

      {/* Smooth Animated Trailing Cursor */}
      <SmoothCursor />

      {/* Native Scroll Progress Indicator */}
      <div className="scroll-progress-bar no-print" aria-hidden="true" />

      {/* Maintenance Mode Banner */}
      {flags.maintenanceMode && (
        <div className="no-print bg-amber-500 text-slate-950 font-bold text-xs py-2 px-4 text-center tracking-wide uppercase shadow-md flex items-center justify-center gap-2">
          <span>⚠️</span> Scheduled System Maintenance Mode is Active
        </div>
      )}

      {/* Route Switch Progress Indicator */}
      <div key={`route-progress-${path}`} className="route-progress-bar no-print" aria-hidden="true" />

      <Header currentPath={path} />
      <AnimatePresence mode="wait">
        <motion.main
          key={`${path}-${section || "root"}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          className="flex-1 flex flex-col bg-transparent"
        >
          {renderPage()}
        </motion.main>
      </AnimatePresence>
      {flags.enableLiveChat && (
        <FloatingWidget currentPath={path} />
      )}
      <Footer />
      <CookieConsent />
    </div>
  );
}
